import useTrialExpiry from "./useTrialExpired";
import { useRouteMatch } from "react-router-dom";
import useModuleInfo from "./useModuleInfo";
import { Modules } from "../util/constants";

/**
 * Hook to determine if the trial banner should be displayed
 * @returns {boolean} - Whether the banner should be displayed
 */
function useDisplayBanner() {
  const { isInTrial, isExpiredTrial } = useTrialExpiry(true);
  const { moduleSubscriptions = [] } = useModuleInfo(undefined, {
    suspense: false,
  });
  const hidePromptBanner = !!useRouteMatch(["/connect"]);
  const welcomeMatch = useRouteMatch({
    path: ["/addorganisation/csvconfiguration", "/select-organisations"],
    exact: false,
  });
  const cashFlowForecastMatch = useRouteMatch({
    path: ["/cashflow/forecast/table", "/cashflow/forecast/chart"],
    exact: false,
  });

  const now = new Date();
  const productModuleSubscriptions = moduleSubscriptions.filter(
    (module) => String(module?.id || "") !== Modules.ORGANISATION,
  );
  const hasModuleSubscriptions = productModuleSubscriptions.length > 0;

  const isExpiredTrialModule = (module) => {
    const trialEndDate = module?.trialEndsAt
      ? new Date(module.trialEndsAt)
      : null;
    if (!trialEndDate || Number.isNaN(trialEndDate.getTime())) return false;
    return !module?.isActive && trialEndDate < now;
  };

  const activeLiveModuleCount = productModuleSubscriptions.filter(
    (module) => Boolean(module?.isActive) && !isExpiredTrialModule(module),
  ).length;

  const trialOrExpiredModuleCount = productModuleSubscriptions.filter(
    (module) => Boolean(module?.isInTrial) || isExpiredTrialModule(module),
  ).length;

  const hasLiveActiveSubscription = activeLiveModuleCount > 0;

  const hasOnlyTrialOrExpiredModules =
    hasModuleSubscriptions &&
    activeLiveModuleCount === 0 &&
    trialOrExpiredModuleCount > 0;

  const hasLegacyTrialState = Boolean(isInTrial || isExpiredTrial);

  const shouldShowByLegacyFlags =
    !hasModuleSubscriptions && hasLegacyTrialState;

  const shouldShowByModuleSubscriptions =
    hasOnlyTrialOrExpiredModules && !hasLiveActiveSubscription;

  return (
    (shouldShowByLegacyFlags || shouldShowByModuleSubscriptions) &&
    !hidePromptBanner &&
    !welcomeMatch &&
    !cashFlowForecastMatch
  );
}

export default useDisplayBanner;
